
import React, { useEffect, useMemo } from "react"
import dayjs from "dayjs";
import { debounce } from "lodash";
import { Form, Input, InputNumber, Select, Button, Cascader, Switch, Radio, Checkbox } from 'antd';
import { language } from '../../locales';
import { DatePicker, RangePicker } from "../datePicker";
import { formItemProps, EditFormProps } from "./data";
import "./index.scss";

import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);

const lang = language?.config.component.editForm;
const langCommon = language?.config.common;

const { Password, TextArea, Search } = Input;
const { Group: RadioGroup } = Radio;
const { Group: CheckboxGroup } = Checkbox;

export type FormConfigType = formItemProps[];

export const EditForm = (props: EditFormProps) => {
  const [defaultForm] = Form.useForm();
  const form = props.form ? props.form : defaultForm;
  const { onDataChange, openDebounce = false } = props;

  let debounceSetData: any = useMemo(() => {
    return onDataChange && debounce(onDataChange, typeof openDebounce === 'number' ? openDebounce : 200);
  }, [onDataChange, openDebounce]);

  const changeValue = (name: any, val: any) => {
    const { formData } = props;
    if (name instanceof Array) {
      name.forEach((e: any, index: number) => {
        formData[e] = val[index];
      })
    } else {
      formData[name] = val;
    }

    if (openDebounce) {
      debounceSetData({ ...formData }, name);
    } else {
      onDataChange && onDataChange({ ...formData }, name);
    }
  }

  useEffect(() => {
    const { getFormUse, formData } = props;
    getFormUse && getFormUse(form);
    const params = { ...formData };
    form && form?.setFieldsValue?.(params);
  }, [form, props])


  const renderComponent = (item: formItemProps) => {
    const { formData, componentId } = props;
    const { field, fields, title, type, extendType, fieldNames, labelInValue, linkageOptions, placeholder, ...otherItems } = item;

    switch (type) {
      case 'input':
        const inputExtends: any = extendType === 'Password' ? Password : extendType === 'TextArea' ?
          TextArea : extendType === 'Search' ? Search : Input;
        return (
          React.createElement(extendType ? inputExtends : Input, {
            id: `${componentId}-commonForm-input-${field}`,
            value: formData[field],
            placeholder: placeholder || `${lang?.inputPlaceholder}${title || field || ''}`,
            onChange: (e: any) => {
              changeValue(field, e.target.value);
            },
            ...otherItems
          })
        );
      case 'inputNumber':
        return (
          <InputNumber
            id={`${componentId}-commonForm-inputNumber-${field}`}
            value={formData[field]}
            placeholder={placeholder || `${lang?.inputPlaceholder}${title || field || ''}`}
            onChange={(e: any) => {
              changeValue(field, e);
            }}
            {...otherItems}
          />
        );
      case 'select':
        return (
          <Select
            id={`${componentId}-commonForm-select-${field}`}
            value={formData[field]}
            placeholder={`${lang?.selectPlaceholder}${title || field || ''}`}
            fieldNames={fieldNames ? fieldNames : { value: 'id', label: 'name' }}
            onChange={(e: any, option: any) => {
              let val = e;
              if (labelInValue && Array.isArray(option)) {
                val = option.map((e: any) => ({
                  ...e,
                  value: e[fieldNames?.value || 'value'],
                  label: e[fieldNames?.label || 'label'],
                }))
              } else if (labelInValue && !Array.isArray(option)) {
                val = {
                  ...e,
                  ...option,
                  value: e[fieldNames?.value || 'value'],
                  label: e[fieldNames?.label || 'label'],
                }
              }
              const value = (item.mode === 'multiple' || item.mode === 'tags') ? (val && !val.length ? undefined : val) : val;
              changeValue(field, value);
            }}
            optionFilterProp={fieldNames ? (fieldNames?.label || 'label') : 'label'}
            labelInValue={labelInValue}
            {...otherItems}
          />
        );
      case 'cascader':
        return (
          <Cascader
            id={`${componentId}-commonForm-cascader-${field}`}
            value={formData[field]}
            placeholder={`${lang?.selectPlaceholder}${title || field || ''}`}
            onChange={(e: any) => {
              changeValue(field, e);
            }}
            showSearch
            {...otherItems}
          />
        );
      case 'switch':
        const { initialValue, ...otherSwitchItems } = otherItems
        return (
          <Switch
            id={`commonForm-switch -${field}`}
            checked={formData[field]}
            defaultChecked={initialValue}
            onChange={(e: any) => {
              changeValue(field, e);
            }}
            {...otherSwitchItems}
          />
        );
      case 'radio':
        return (
          <RadioGroup
            id={`commonForm-radioGroup -${field}`}
            value={formData[field]}
            onChange={(e: any) => {
              changeValue(field, e.target.value);
            }}
            {...otherItems}
            options={(linkageOptions && linkageOptions(formData)) || item.options}
          />
        );
      case 'checkbox':
        return (
          <CheckboxGroup
            value={formData[field]}
            onChange={(e: any) => {
              changeValue(field, e);
            }}
            {...otherItems}
            options={(linkageOptions && linkageOptions(formData)) || item.options}
          />
        );
      case 'datePicker':
        let date = formData[field] || null;
        if (date) {
          date = dayjs(date);
        }
        return (
          <DatePicker
            value={date}
            id={`commonForm-datePicker -${field}`}
            onChange={(_: any, dateString: any) => {
              changeValue(field, dateString || null);
            }}
            placeholder={`${lang?.selectPlaceholder}${title || field || ''}`}
            {...otherItems}
          />
        );
      case 'rangePicker':
        const startField = (fields && fields[0]) || '';
        const endField = (fields && fields[1]) || '';
        const startVal = formData?.[startField] || null;
        const endVal = formData?.[endField] || null;

        return (
          <RangePicker
            pickerValue={(startVal && endVal) ? [startVal, endVal] : null}
            value={(startVal && endVal) ? [startVal, endVal] : null}
            id={`commonForm-rangePicker-${field}`}
            onChange={(_: any, dateString: string) => {
              changeValue(fields, dateString);
              // changeValue(field, dateString);
            }}
            {...otherItems}
          />
        );
      default:
        break;
    }
  }

  const formatRules = (item: formItemProps) => {
    const { rules = [], fields = [], type, title } = item;
    if (type === 'rangePicker' && rules?.[0]?.required) {
      const startField = (fields && fields[0]) || '';
      const endField = (fields && fields[1]) || '';
      const startVal = formData?.[startField] || null;
      const endVal = formData?.[endField] || null;
      const currentRules = [...rules];
      currentRules[0] = {
        validator: async () => {
          if (!startVal || !endVal) {
            return Promise.reject(currentRules[0]?.message || `${langCommon?.pleaseSelect}${typeof title === 'string' ? title : ''}`)
          } else {
            return Promise.resolve();
          }
        },
        ...currentRules[0]
      }
      return currentRules;
    } else {
      return rules;
    }
  }

  const { componentId, formAttrsProps, formConfig, slot, loading, onQuery, onReset, formData, className } = props;

  return (
    <div className="commonForm">
      <Form
        id={componentId}
        name={componentId}
        autoComplete="off"
        className={className}
        form={form}
        {...formAttrsProps}
      >
        {
          formConfig?.map((item: formItemProps) => {

            return (
              <Form.Item
                key={item.field}
                label={item.title}
                {...(item.field ? { name: item.field } : {})}
                colon={item.colon}
                required={item.required}
                rules={formatRules(item)}
                hidden={item.hidden}
                htmlFor={item.htmlFor}
                initialValue={item.initialValue}
                labelAlign={item.labelAlign}
                labelCol={item.labelCol}
                wrapperCol={item.wrapperCol}
                style={item.itemStyle}
                validateTrigger={item.validateTrigger}
                extra={item.extra}
              >
                {item.render ? item.render(formData, changeValue, form) : renderComponent(item)}
              </Form.Item>
            );
          })
        }
        {onQuery ? (
          <Button type="primary" className="mr10" loading={loading}
            onClick={onQuery}
          >{langCommon?.query}</Button>
        ) : null}
        {onReset ? (
          <Button loading={loading}
            onClick={() => {
              // form.resetFields();
              onReset && onReset();
            }}
          >{langCommon?.reset}</Button>
        ) : null}
        {slot}
      </Form>
    </div>
  );
}
